/**
 * Куб.Мета theme for the Sentry User Feedback widget (the "Сообщить о проблеме" form).
 *
 * The widget renders inside an open Shadow DOM under `#sentry-feedback`, so Tailwind classes and
 * our global stylesheets cannot reach it. Sentry exposes CSS custom-property hooks (`--dialog-*`,
 * `--input-*`, `--button-*`, see @sentry-internal/feedback createDialogStyles) plus stable BEM
 * classes (`.dialog__content`, `.form__input`, `.btn--primary`, `.btn--default`). We append one
 * <style> to the shadow root: variables on `:host`, then a few rules for what the variables
 * cannot express. Values follow Figma node 4059:10733 and the libs/tailwind tokens (mlight,
 * mdark, mblue, --shadow-light).
 */

const STYLE_ID = "cubemeta-sentry-feedback-theme";

const FEEDBACK_THEME_CSS = `
:host {
  --font-family: "Vela Sans", system-ui, sans-serif;
  --font-size: 14px;
  --page-margin: 24px;

  --background: #f2f3ff; /* mlight */
  --foreground: #292d32; /* mdark */
  --accent-background: #5f6fff; /* mblue */
  --accent-foreground: #fff;
  --success-color: #2bbf14; /* mgreen */
  --error-color: #fb2c36; /* mdanger */
  --border: none;
  --outline: 2px solid #5f6fff;
  --interactive-filter: none;

  --dialog-padding: 32px;
  --dialog-border-radius: 24px;
  --dialog-box-shadow: -2px -2px 4px 0 #5f6fff0d inset, 3px 3px 4px 0 #fff inset, 0 2px 3px 0 #5f6fff26; /* --shadow-light */
  --dialog-header-weight: 700;
  --form-width: 360px;

  --input-border: 1px solid #e1e8ff; /* mlight-darker */
  --input-border-radius: 8px;
  --input-font-size: 16px;
  --input-font-weight: 400;
  --input-placeholder-color: #525252; /* mdark-lighter */
  --input-focus-outline: none;

  --button-border-radius: 16px;
  --button-font-weight: 500;
  --button-color: #5f6fff;
  --button-background: transparent;
  --button-hover-background: rgba(70, 89, 255, 0.05); /* mblue-darker/5 */
  --button-border: 1px solid #5f6fff;
  --button-primary-border-radius: 16px;
  --button-primary-border: none;
  --button-primary-font-weight: 500;
  --button-primary-hover-background: #4659ff; /* mblue-darker */
}

@media (max-width: 600px) {
  :host {
    --form-width: 100%;
  }
}

.dialog {
  background-color: transparent;
}
.dialog__content {
  gap: 24px;
}
.dialog__title {
  font-size: 24px;
  line-height: 32px;
}
.form__right {
  gap: 24px;
}
.form__top {
  gap: 16px;
}
.form__label {
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
}
.form__input {
  background: #fff;
  padding: 13px 15px;
  line-height: 20px;
}
.form__input::placeholder {
  opacity: 1;
}
.form__input:focus-visible {
  border-color: #5f6fff;
}
.form__input--textarea {
  height: 140px;
}
.btn--primary {
  min-height: 44px;
  padding: 12px 20px;
  font-size: 18px;
  line-height: 17px;
}
.btn--default {
  padding: 11px 15px;
  font-size: 14px;
  line-height: 1.2;
}
`;

/**
 * Appends (or moves to the end of) our theme <style> in the widget's shadow root.
 * Pass `form.el.getRootNode()` after `form.appendToDom()`: Sentry appends its own dialog <style>
 * there on every appendToDom(), and with equal specificity the later stylesheet wins, so ours is
 * (re-)appended each time to keep it last. Not looked up by `#sentry-feedback` id on purpose —
 * every feedbackIntegration() instance creates its own host with that id.
 */
export function applySentryFeedbackTheme(root: Node): void {
    if (!(root instanceof ShadowRoot)) {
        return;
    }
    const shadowRoot = root;
    let style = shadowRoot.getElementById(STYLE_ID);
    if (!style) {
        style = document.createElement("style");
        style.id = STYLE_ID;
        style.textContent = FEEDBACK_THEME_CSS;
    }
    shadowRoot.appendChild(style);
}
